import { useEffect, useRef, useState } from 'react';
import { Download, Eye, File } from 'lucide-react';
import type { ClientUploadFile } from '@shared/client-uploads';
import { uploadSize } from './public-api';

export type UploadPreviewValue = { fileName: string; url: string; mimeType: string };
export type UploadLinkResolver = (file: ClientUploadFile, download: boolean) => Promise<{ url: string; mimeType: string }>;
const smallImage = (file: ClientUploadFile) => file.sizeBytes <= 10_000_000 && /^image\/(?:jpeg|png|gif|webp|avif)$/.test(file.mimeType || '');

export function UploadPreview({ value, onClose, language = 'de' }: { value: UploadPreviewValue; onClose: () => void; language?: 'de' | 'en' }) {
  const english = language === 'en';
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
      if (event.key === 'Tab') {
        const nodes = panel.current?.querySelectorAll<HTMLElement>('button,a[href],video,audio,iframe');
        if (!nodes?.length) return;
        if (event.shiftKey && document.activeElement === nodes[0]) { event.preventDefault(); nodes[nodes.length - 1].focus(); }
        else if (!event.shiftKey && document.activeElement === nodes[nodes.length - 1]) { event.preventDefault(); nodes[0].focus(); }
      }
    };
    document.addEventListener('keydown', key);
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', key); previous?.focus(); };
  }, []);
  const image = /^image\/(?:jpeg|png|gif|webp|avif)$/.test(value.mimeType);
  return (
    <div ref={panel} className="cu-modal" role="dialog" aria-modal="true" aria-label={`${english ? 'Preview' : 'Vorschau'}: ${value.fileName}`}>
      <div className="cu-modal-header"><strong>{value.fileName}</strong><button ref={closeButton} type="button" className="cu-text-button" onClick={onClose}>{english ? 'Close' : 'Schließen'}</button></div>
      <div className="cu-modal-content">
        {failed ? <p className="cu-muted">{english ? 'Preview could not load. Close and try again, or download the file.' : 'Die Vorschau konnte nicht geladen werden. Bitte schließen und erneut öffnen oder die Datei herunterladen.'}</p>
          : image ? <img src={value.url} alt={value.fileName} onError={() => setFailed(true)} />
          : value.mimeType.startsWith('video/') ? <video controls preload="metadata" src={value.url} onError={() => setFailed(true)} />
          : value.mimeType.startsWith('audio/') ? <audio controls preload="metadata" src={value.url} onError={() => setFailed(true)} />
          : value.mimeType === 'application/pdf' ? <iframe title={value.fileName} src={value.url} sandbox="allow-scripts allow-same-origin" />
          : <p className="cu-muted">{english ? 'No browser preview for this format. Download the approved file instead.' : 'Für dieses Format gibt es keine Browser-Vorschau. Nutze den Download der freigegebenen Datei.'}</p>}
      </div>
    </div>
  );
}

export default function UploadHistoryFile({ file, link, onPreview, onError, compact = false, language = 'de' }: {
  file: ClientUploadFile; link: UploadLinkResolver;
  onPreview: (value: UploadPreviewValue) => void; onError: (error: string) => void;
  compact?: boolean; language?: 'de' | 'en';
}) {
  const [thumb, setThumb] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const callbacks = useRef({ link, onError });
  callbacks.current = { link, onError };
  useEffect(() => {
    let active = true;
    setThumb(null);
    if (smallImage(file) && ['approved', 'available'].includes(file.state)) {
      void callbacks.current.link(file, false).then(result => {
        if (active && /^image\/(?:jpeg|png|gif|webp|avif)$/.test(result.mimeType)) setThumb(result.url);
      }).catch(() => {});
    }
    return () => { active = false; };
  }, [file.id, file.state, file.mimeType, file.sizeBytes]);
  const open = async (download: boolean) => {
    if (busy || !['approved', 'available'].includes(file.state)) return;
    setBusy(true);
    try {
      const result = await link(file, download);
      if (download) {
        const anchor = document.createElement('a');
        anchor.href = result.url; anchor.download = file.fileName; anchor.rel = 'noopener noreferrer';
        anchor.target = '_blank'; document.body.appendChild(anchor); anchor.click(); anchor.remove();
      } else onPreview({ ...result, fileName: file.fileName });
    } catch (err) { onError((err as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <div className={compact ? 'cu-file-compact' : 'cu-file'}>
      {thumb ? <img className="cu-thumb" loading="lazy" src={thumb} alt="" onError={() => setThumb(null)} /> : <span className="cu-thumb cu-file-symbol">{compact ? <File size={16} aria-hidden="true" /> : language === 'en' ? 'FILE' : 'DATEI'}</span>}
      {!compact && <div className="cu-file-main"><strong className="cu-file-name">{file.fileName}</strong><span className="cu-file-meta">{uploadSize(file.sizeBytes)}{file.uploadedAt && ` · ${new Date(file.uploadedAt).toLocaleString(language === 'en' ? 'en-GB' : 'de-DE')}`}</span></div>}
      <div className="cu-actions"><button type="button" disabled={busy || !['approved', 'available'].includes(file.state)} className="cu-text-button" onClick={() => void open(false)}>{compact && <Eye size={13} aria-hidden="true" />}{language === 'en' ? 'Preview' : 'Vorschau'}</button><button type="button" disabled={busy || !['approved', 'available'].includes(file.state)} className="cu-text-button" onClick={() => void open(true)}>{compact && <Download size={13} aria-hidden="true" />}Download</button></div>
    </div>
  );
}